import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ApiClass } from '@class/ApiClass';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { WebSocketService } from './websocket.service';

export type Notification = {
  id: number;
  ticket_id: number;
  name: string;
  description: string;
  created_at: string;
  is_read: boolean;
  ticket: {
    id: number;
    folio: string;
  }
};

type NotificationResponse = {
  items: Notification[];
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends ApiClass {
  public notifications: Observable<Notification[]>;
  private notificationsSubject: BehaviorSubject<Notification[]>;

  public notificationCount: Observable<number>;
  private notificationCountSubject: BehaviorSubject<number>;

  public isPanelOpen: Observable<boolean>;
  private isPanelOpenSubject: BehaviorSubject<boolean>;

  constructor(private _title: Title, private authService: AuthenticationService, private websocketService: WebSocketService) {
    super();
    this.notificationsSubject = new BehaviorSubject<Notification[]>([]);
    this.notifications = this.notificationsSubject.asObservable();
    this.notificationCountSubject = new BehaviorSubject<number>(0);
    this.notificationCount = this.notificationCountSubject.asObservable();
    this.isPanelOpenSubject = new BehaviorSubject<boolean>(false);
    this.isPanelOpen = this.isPanelOpenSubject.asObservable();

    this.authService.currentUser.subscribe(async (auth) => {
      try {
        if(auth) {
            if (this.websocketService.isLoaded()) return;
            const notifications = await this._getNotifications().toPromise();
            await this._updateNotifications(notifications);
            if (this.websocketService.isLoaded()) return;
            else this.websocketService.subscribeToPrivateNotificationsChannel(auth.user.id, this._addNotification.bind(this))
          }
      } catch (error) {
        console.table(error);
      }
    });
  }

  private _addNotification(notification: Notification) {
    const count = this.notificationCountSubject.value + 1;
    this.notificationsSubject.next([notification, ...this.notificationsSubject.value]);
    this.notificationCountSubject.next(count);
    this.updateTitle(count);
  }

  public togglePanel() {
    this.isPanelOpenSubject.next(!this.isPanelOpenSubject.value);
    this._toggleOverflows();
  }

  public markAsRead(notification: Notification) {
    if (!notification.is_read) {
      this.patch<NotificationResponse>(
        `/notifications/${notification.id}`
      ).subscribe({
        next: () => {
          const count = this.notificationCountSubject.value - 1;
          this.notificationsSubject.next(
            this.notificationsSubject.value.map((n) =>
              n.id === notification.id ? { ...n, is_read: true } : n
              )
          );
          this.notificationCountSubject.next(count);
          this.updateTitle(count);
        }
      });
    }
  }

  private _getNotifications() {
    return this.get<NotificationResponse>('/user/notifications');
  }

  private _updateNotifications(notifications: NotificationResponse) {
    this.notificationsSubject.next(notifications.items);
    this.notificationCountSubject.next(notifications.count);
    this.updateTitle(notifications.count);
  }

  public updateTitle(count: number) {
    if (count > 0) {
      if (this._title.getTitle().match(/^\(\d+\)\s/)) {
        this._title.setTitle(
          this._title.getTitle().replace(/^\(\d+\)\s/, `(${count}) `)
        );
      } else {
        this._title.setTitle(`(${count}) ${this._title.getTitle()}`);
      }
    } else {
      this._title.setTitle(this._title.getTitle().replace(/^\(\d+\)\s/, ''));
    }
  }

  private _toggleOverflows() {
    const body = document.querySelector('body');
    if (this.isPanelOpenSubject.value) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = 'auto';
    }
  }

  public get isOpen() {
    return this.isPanelOpenSubject.value;
  }
}
